/**
 * 共通のクエリキーとクエリフック。
 * 従来は各画面が同じ queryKey/queryFn を個別に書いており、キーや型のずれの温床だった（audit E3/E4）
 */

import { useQuery } from '@tanstack/react-query'
import { apiClient, unwrap } from './api'
import type {
  AccountSettings, Channel, ImageStyle, Persona, PostRow, QueueItem, WritingFormat,
} from './postTypes'

export const queryKeys = {
  queue: ['queue'] as const,
  credits: ['credits'] as const,
  settings: ['settings'] as const,
  writingFormats: ['writing-formats'] as const,
  imageStyles: ['image-styles'] as const,
  komaNumberStyles: ['koma-number-styles'] as const,
  personas: ['personas'] as const,
  channels: (personaId: string) => ['channels', personaId] as const,
  scheduledPosts: ['posts', 'scheduled'] as const,
  estimate: (kind: string, imageCount: number) => ['estimate', kind, imageCount] as const,
}

export function useQueue(opts?: { refetchInterval?: number }) {
  return useQuery({
    queryKey: queryKeys.queue,
    queryFn: async () => unwrap(await apiClient.get<QueueItem[]>('/queue')),
    refetchInterval: opts?.refetchInterval,
  })
}

export function useCredits(opts?: { refetchInterval?: number; staleTime?: number }) {
  return useQuery({
    queryKey: queryKeys.credits,
    queryFn: async () => unwrap(await apiClient.get<{ balance: number }>('/account/credits/balance')),
    refetchInterval: opts?.refetchInterval,
    staleTime: opts?.staleTime,
  })
}

export function useSettings() {
  return useQuery({
    queryKey: queryKeys.settings,
    queryFn: async () => unwrap(await apiClient.get<AccountSettings>('/settings')),
  })
}

export function useFormats() {
  return useQuery({
    queryKey: queryKeys.writingFormats,
    queryFn: async () => unwrap(await apiClient.get<WritingFormat[]>('/settings/writing-formats')),
  })
}

export function useImageStyles() {
  return useQuery({
    queryKey: queryKeys.imageStyles,
    queryFn: async () => unwrap(await apiClient.get<ImageStyle[]>('/settings/image-styles')),
  })
}

/** 番号デザインの一覧はサーバーが真実源（GET /settings/koma-number-styles） */
export function useKomaNumberStyles() {
  return useQuery({
    queryKey: queryKeys.komaNumberStyles,
    queryFn: async () =>
      unwrap(await apiClient.get<Array<{ key: string; label: string }>>('/settings/koma-number-styles')),
  })
}

export function usePersonas() {
  return useQuery({
    queryKey: queryKeys.personas,
    queryFn: async () => unwrap(await apiClient.get<Persona[]>('/personas')),
  })
}

export function useChannels(personaId: string) {
  return useQuery({
    queryKey: queryKeys.channels(personaId),
    enabled: !!personaId,
    queryFn: async () =>
      unwrap(await apiClient.get<Channel[]>(`/channels?personaId=${encodeURIComponent(personaId)}`)),
  })
}

export function useScheduledPosts() {
  return useQuery({
    queryKey: queryKeys.scheduledPosts,
    queryFn: async () => unwrap(await apiClient.get<PostRow[]>('/posts?status=scheduled')),
  })
}

export function useEstimate(kind: string, imageCount: number) {
  return useQuery({
    queryKey: queryKeys.estimate(kind, imageCount),
    queryFn: async () =>
      unwrap(await apiClient.get<{ credits: number }>(`/posts/estimate?kind=${kind}&imageCount=${imageCount}`)),
  })
}
